import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from 'react-router'
import { Link } from 'react-router'

import insightsDarkmode from '@/assets/graphs/insights-p50-p95-p99-darkmode.png'
import insights from '@/assets/graphs/insights-p50-p95-p99.png'
import social from '@/assets/homepage-social.png?url'
import Logo, { type Props as LogoProps } from '@/components/Logo'
import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import VitessDiagram from '@/components/VitessDiagram'
import { cacheControl } from '@/http.server'
import { pageHelper } from '@/request'

type LoaderData = typeof loader

export async function loader({ url }: LoaderFunctionArgs) {
  const { root } = pageHelper(url)

  return { root: root }
}

export const headers: HeadersFunction = () => ({
  'Cache-Control': cacheControl,
  Link: [
    '</docs/openapi.yaml>; rel="service-desc"; type="application/yaml"',
    '</docs>; rel="service-doc"',
    '</llms.txt>; rel="describedby"; type="text/plain"'
  ].join(', ')
})

export const meta: MetaFunction<LoaderData> = ({ loaderData }) => {
  if (!loaderData) return []

  const { root } = loaderData

  const title = 'PlanetScale - the world’s fastest and most scalable cloud hosting for Vitess, Neki, and Postgres'
  const description =
    'PlanetScale offers the world’s fastest and most scalable cloud hosting for Vitess, Neki, and Postgres.'
  const image = `${root}${social}`

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:url', content: 'https://planetscale.com' },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: title },
    { property: 'og:image', content: image },
    { property: 'og:description', content: description },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@PlanetScale' },
    { property: 'twitter:creator', content: '@PlanetScale' },
    { property: 'twitter:url', content: 'https://planetscale.com' },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: description },
    { property: 'twitter:image', content: image },
    { tagName: 'link', rel: 'canonical', href: 'https://planetscale.com' }
  ]
}

type LogoWallEntry = {
  name: LogoProps['name']
  ariaLabel: string
  darkMode?: boolean
  size?: string
  link?: { to: string; label: string } | { href: string; label: string }
}

const defaultLogoSize = 'max-h-12 w-3/4 max-w-20'

const logoWall: LogoWallEntry[] = [
  {
    name: 'square',
    ariaLabel: 'Square',
    darkMode: true,
    size: 'max-h-12 w-3/4 max-w-28'
  },
  {
    name: 'cash-app',
    ariaLabel: 'Cash App',
    darkMode: true,
    size: 'max-h-12 w-3/4 max-w-28',
    link: { to: '/case-studies/cash-app', label: 'Read case study' }
  },
  { name: 'cursor', ariaLabel: 'Cursor', darkMode: true },
  {
    name: 'fin',
    ariaLabel: 'Fin',
    darkMode: true,
    link: { href: 'https://pscale.link/int', label: 'Read case study' }
  },
  { name: 'etsy', ariaLabel: 'Etsy' },
  { name: 'kick', ariaLabel: 'Kick', size: 'max-h-12 w-3/5 max-w-20' },
  { name: 'whop', ariaLabel: 'Whop', darkMode: true },
  { name: 'cloudflare', ariaLabel: 'Cloudflare', darkMode: true, size: 'max-h-12 w-3/4 max-w-40' },
  { name: 'whatnot', ariaLabel: 'Whatnot', darkMode: true },
  { name: 'slack', ariaLabel: 'Slack', darkMode: true },
  { name: 'mercor', ariaLabel: 'Mercor' },
  { name: 'notion', ariaLabel: 'Notion', darkMode: true, size: 'max-h-12 w-3/4 max-w-24' },
  { name: 'laravel', ariaLabel: 'Laravel' },
  { name: 'clay', ariaLabel: 'Clay', darkMode: true, size: 'max-h-12 w-3/4 max-w-16' },
  { name: 'clerk', ariaLabel: 'Clerk', darkMode: true },
  { name: 'orb', ariaLabel: 'Orb', darkMode: true, size: 'max-h-12 w-3/4 max-w-16' },
  { name: 'typesafe-ai', ariaLabel: 'TypeSafe AI', darkMode: true, size: 'max-h-12 w-3/4 max-w-20 scale-[0.81]' },
  { name: 'grok-bot', ariaLabel: 'Grok Bot', darkMode: true },
  { name: 'poke', ariaLabel: 'Poke by Interaction', darkMode: true },
  { name: 'resend', ariaLabel: 'Resend', darkMode: true, size: 'max-h-12 w-3/4 max-w-24' },
  { name: 'mixpanel', ariaLabel: 'Mixpanel', darkMode: true, size: 'max-h-12 w-3/4 max-w-24' },
  { name: 'profound', ariaLabel: 'Profound', darkMode: true, size: 'max-h-12 w-3/4 max-w-24' },
  { name: 'opencode', ariaLabel: 'OpenCode', darkMode: true },
  { name: 'gigaml', ariaLabel: 'GigaML', darkMode: true, size: 'max-h-12 w-3/4 max-w-24' },
  { name: 'lime', ariaLabel: 'Lime' },
  { name: 'pylon', ariaLabel: 'Pylon', darkMode: true, size: 'max-h-10 w-3/4 max-w-24' },
  { name: 'astral', ariaLabel: 'Astral', darkMode: true },
  { name: 'valon', ariaLabel: 'Valon', darkMode: true },
  { name: 'vallor', ariaLabel: 'Vallor', darkMode: true },
  { name: 'lmnt', ariaLabel: 'LMNT', darkMode: true, size: 'max-h-10 w-3/4 max-w-10' },
  { name: 'runpod', ariaLabel: 'Runpod', darkMode: true, size: 'max-h-12 w-3/4 max-w-24' },
  {
    name: 'standard-intelligence',
    ariaLabel: 'Standard Intelligence',
    darkMode: true,
    size: 'max-h-12 w-3/4 max-w-20 scale-[0.81]'
  },
  { name: 'turbopuffer', ariaLabel: 'turbopuffer', darkMode: true },
  { name: 'kernel', ariaLabel: 'KERNEL', darkMode: true, size: 'max-h-10 w-3/4 max-w-40' },
  { name: 'conductor', ariaLabel: 'Conductor', darkMode: true, size: 'max-h-12 w-3/4 max-w-32' },
  { name: 'inngest', ariaLabel: 'Inngest', darkMode: true },
  { name: 'heygen', ariaLabel: 'HeyGen', darkMode: true, size: 'max-h-12 w-3/4 max-w-24' },
  { name: 'same', ariaLabel: 'Same', darkMode: true, size: 'max-h-12 w-3/4 max-w-16' },
  { name: 'classdojo', ariaLabel: 'ClassDojo', darkMode: true },
  { name: 'tomoai', ariaLabel: 'TomoAI', darkMode: true, size: 'max-h-12 w-3/4 max-w-24' },
  {
    name: 'depot',
    ariaLabel: 'Depot',
    darkMode: true,
    size: 'max-h-12 w-3/4 max-w-24',
    link: { href: 'https://depot.dev/blog/faster-database-with-planetscale-metal', label: 'Read blog post' }
  },
  { name: 'greptile', ariaLabel: 'Greptile', darkMode: true, size: 'max-h-12 w-3/4 max-w-28' },
  { name: 'blacksmith', ariaLabel: 'Blacksmith', darkMode: true, size: 'max-h-12 w-3/4 max-w-28' },
  {
    name: 'supermemory',
    ariaLabel: 'Supermemory',
    darkMode: true,
    link: { href: 'https://supermemory.ai/blog/supermemory-just-got-faster-on-planetscale/', label: 'Read blog post' }
  },
  { name: 'bento', ariaLabel: 'Bento', darkMode: true, size: 'max-h-10 w-3/4 max-w-28' },
  { name: 'polar', ariaLabel: 'Polar', darkMode: true, size: 'max-h-12 w-3/4 max-w-24' },
  { name: 'gitbutler', ariaLabel: 'GitButler', darkMode: true, size: 'max-h-10 w-3/4 max-w-28' },
  { name: 'lugg', ariaLabel: 'Lugg', darkMode: true, size: 'max-h-12 w-3/4 max-w-16' },
  {
    name: 'dub',
    ariaLabel: 'Dub',
    darkMode: true,
    size: 'max-h-10 w-3/4 max-w-14',
    link: { to: '/case-studies/dub', label: 'Read case study' }
  },
  { name: 'executor', ariaLabel: 'Executor', darkMode: true, size: 'max-h-12 w-3/4 max-w-24' },
  {
    name: 'trigger-dev',
    ariaLabel: 'Trigger.dev',
    darkMode: true,
    size: 'max-h-10 w-3/4 max-w-28',
    link: { href: 'https://trigger.dev/blog/upgrading-a-56tb-database', label: 'Read blog post' }
  },
  { name: 'peregrine', ariaLabel: 'Peregrine.io', darkMode: true, size: 'max-h-10 w-3/4 max-w-28' },
  { name: 'buttondown', ariaLabel: 'Buttondown', size: 'max-h-10' },
  { name: 'octolens', ariaLabel: 'Octolens', darkMode: true, size: 'max-h-10 w-3/4 max-w-28' },
  { name: 'vetcove', ariaLabel: 'Vetcove', darkMode: true },
  { name: 'fathom', ariaLabel: 'Fathom Analytics', darkMode: true },
  { name: 'scale-to-win', ariaLabel: 'Scale to Win', darkMode: true, size: 'max-h-12 w-3/4 max-w-14' },
  { name: 'sfcompute', ariaLabel: 'SF Compute', darkMode: true, size: 'max-h-10 w-3/4 max-w-10' },
  { name: 'layers', ariaLabel: 'Layers', darkMode: true, size: 'max-h-10 w-3/4 max-w-32' },
  { name: 'firecrawl', ariaLabel: 'Firecrawl', darkMode: true, size: 'max-h-10 w-3/4 max-w-24' },
  { name: 'owner', ariaLabel: 'Owner.com', darkMode: true },
  {
    name: 'myfitnesspal',
    ariaLabel: 'MyFitnessPal',
    darkMode: true,
    link: { to: '/case-studies/myfitnesspal', label: 'Read case study' }
  },
  {
    name: 'barstool',
    ariaLabel: 'Barstool Sports',
    darkMode: true,
    link: { to: '/case-studies/barstool-sports', label: 'Read case study' }
  },
  { name: 'entire', ariaLabel: 'Entire', darkMode: true, size: 'max-h-10 w-3/4 max-w-24' },
  { name: 'pocket', ariaLabel: 'Pocket', darkMode: true },
  { name: 'hoplite', ariaLabel: 'Hoplite', darkMode: true, size: 'max-h-10 w-3/4 max-w-10' },
  { name: 'photon', ariaLabel: 'Photon', darkMode: true, size: 'max-h-12 w-3/4 max-w-24' },
  { name: 'unkey', ariaLabel: 'Unkey', darkMode: true, size: 'max-h-12 w-3/4 max-w-24' },
  { name: 'day-ai', ariaLabel: 'Day.ai', darkMode: true, size: 'max-h-12 w-3/4 max-w-24' },
  { name: 'quiverai', ariaLabel: 'QuiverAI', darkMode: true },
  { name: 'atuin', ariaLabel: 'Atuin', darkMode: true },
  { name: 'delphi', ariaLabel: 'Delphi', darkMode: true, size: 'max-h-12 w-3/4 max-w-14' },
  {
    name: 'autumn',
    ariaLabel: 'Autumn',
    darkMode: true,
    link: { href: 'https://useautumn.com/blog/migrating-to-planetscale', label: 'Read case study' }
  },
  { name: 'clearstream', ariaLabel: 'Clearstream', darkMode: true, size: 'max-h-12 w-3/4 max-w-28' },
  { name: 'capy', ariaLabel: 'Capy', darkMode: true, size: 'max-h-10 w-3/4 max-w-24' },
  { name: 'mothership', ariaLabel: 'Mothership', darkMode: true, size: 'max-h-10 w-3/4 max-w-32' },
  { name: 'sazabi', ariaLabel: 'Sazabi', size: 'max-h-10 w-3/4 max-w-28' },
  { name: 'proximal-labs', ariaLabel: 'Proximal Labs', darkMode: true, size: 'max-h-10 w-3/4 max-w-32' },
  { name: 'haloai', ariaLabel: 'Halo AI', darkMode: true, size: 'max-h-12 w-3/4 max-w-12' },
  { name: 'everai', ariaLabel: 'EverAI', darkMode: true, size: 'max-h-10 w-3/4 max-w-28' },
  {
    name: 'mixedbread',
    ariaLabel: 'Mixedbread',
    darkMode: true,
    size: 'max-h-10 w-3/4 max-w-28',
    link: { href: 'https://www.mixedbread.com/blog/planetscale-case-study', label: 'Read blog post' }
  },
  { name: 'primitive', ariaLabel: 'Primitive', size: 'max-h-10 w-3/4 max-w-16' },
  { name: 'mora', ariaLabel: 'Mora', darkMode: true, size: 'max-h-10 w-3/4 max-w-24' },
  { name: 'inth', ariaLabel: 'Inth', darkMode: true, size: 'max-h-12 w-3/4 max-w-24' },
  { name: 'aimventory', ariaLabel: 'Aimventory', darkMode: true, size: 'max-h-12 w-3/4 max-w-28' }
]

const cellBase = 'm-[-0.5px] flex h-12 items-center justify-center border md:h-16'
const cellLink = 'group relative transition hover:bg-gray-100/50 dark:hover:bg-black'

function LogoCell({ entry }: { entry: LogoWallEntry }) {
  const size = entry.size ?? defaultLogoSize

  const inner = (
    <>
      <Logo name={entry.name} ariaLabel={entry.ariaLabel} className={entry.darkMode ? `${size} dark:hidden` : size} />
      {entry.darkMode && (
        <Logo
          name={`${entry.name}-darkmode` as LogoProps['name']}
          ariaLabel={entry.ariaLabel}
          className={`hidden ${size} dark:block`}
        />
      )}
      {entry.link && (
        <span className='absolute bottom-sm text-secondary opacity-0 transition group-hover:opacity-100'>
          {entry.link.label}
        </span>
      )}
    </>
  )

  if (entry.link && 'to' in entry.link) {
    return (
      <Link to={entry.link.to} prefetch='intent' title={entry.ariaLabel} className={`${cellBase} ${cellLink}`}>
        {inner}
      </Link>
    )
  }

  if (entry.link && 'href' in entry.link) {
    return (
      <a
        href={entry.link.href}
        rel='nofollow noopener noreferrer'
        target='_blank'
        title={entry.ariaLabel}
        className={`${cellBase} ${cellLink}`}
      >
        {inner}
      </a>
    )
  }

  return (
    <div title={entry.ariaLabel} className={cellBase}>
      {inner}
    </div>
  )
}

export default function Homepage() {
  return (
    <>
      <SiteHeader />

      <main className='container mb-6 max-w-7xl px-3 sm:px-5 lg:px-12'>
        <section>
          <div className='flex flex-col gap-y-3'>
            <div>
              <h1>The world’s fastest and most scalable cloud databases</h1>
              <p>
                PlanetScale brings you the{' '}
                <Link prefetch='intent' to='/blog/benchmarking-postgres'>
                  fastest databases
                </Link>{' '}
                available in the cloud, built for exceptional speed and reliability. For workloads that outgrow a single
                node, Vitess and Neki, our sharded MySQL and sharded Postgres products, add horizontal scaling, online
                schema changes, no-downtime upgrades, built-in connection pooling, and more.
              </p>
              <p>
                Our blazing fast{' '}
                <Link prefetch='intent' to='/metal'>
                  NVMe drives
                </Link>{' '}
                unlock <strong>unlimited IOPS</strong>, bringing data center performance to the cloud. We offer a range
                of <a href='/docs/plans/deployment-options'>deployment options</a> to cover all of your security and
                compliance requirements &mdash; including bring your own cloud with{' '}
                <a href='/docs/plans/managed'>PlanetScale Managed</a>.
              </p>

              <p>
                PlanetScale powers{' '}
                <Link prefetch='intent' to='/case-studies/cash-app'>
                  Tier 0 databases
                </Link>{' '}
                at:
              </p>

              <div className='mb-3 grid grid-cols-2 items-center justify-center md:grid-cols-5'>
                {logoWall.map((entry) => (
                  <LogoCell key={entry.name} entry={entry} />
                ))}
                {Array.from({ length: (5 - (logoWall.length % 5)) % 5 }).map((_, index) => (
                  <div
                    key={`logo-wall-desktop-placeholder-${index}`}
                    aria-hidden
                    className={`${cellBase} hidden md:flex`}
                  />
                ))}
                {logoWall.length % 2 === 1 && <div aria-hidden className={`${cellBase} md:hidden`} />}
              </div>

              <blockquote className='mt-2 mb-2'>
                We chose PlanetScale to host our most demanding Vitess and Postgres workloads, doing millions of queries
                per second on petabytes of data.
              </blockquote>
              <p className='mt-1 mb-2 text-secondary'>– Sualeh Asif - Chief Product Officer @Anysphere (Cursor)</p>

              <TabGroup defaultIndex={0} className='mt-6'>
                <TabList className='grid grid-cols-1 divide-y border-x border-t md:grid-cols-3 md:divide-x md:divide-y-0'>
                  <Tab className='py-2 text-center font-semibold data-selected:bg-inverted data-selected:text-inverted'>
                    Vitess
                  </Tab>
                  <Tab className='py-2 text-center font-semibold data-selected:bg-inverted data-selected:text-inverted'>
                    Neki
                  </Tab>
                  <Tab className='py-2 text-center font-semibold data-selected:bg-inverted data-selected:text-inverted'>
                    Postgres
                  </Tab>
                </TabList>

                <TabPanels className='border p-3'>
                  <TabPanel unmount={false}>
                    <p>
                      <Link prefetch='intent' to='/vitess'>
                        Vitess
                      </Link>{' '}
                      allows MySQL databases to{' '}
                      <a href='/docs/vitess/sharding/sharding-quickstart'>
                        scale horizontally through explicit sharding
                      </a>{' '}
                      &mdash; enabling a shared nothing architecture distributing data across thousands of nodes, all
                      routed through a single database connection.
                    </p>

                    <p>
                      Vitess was developed at YouTube by the founders of PlanetScale to scale their main MySQL database
                      to petabytes of data on 70,000 nodes across 20 data centers. Now maintained and managed by
                      PlanetScale, Vitess powers the databases of some of the web&apos;s largest properties: Slack,
                      HubSpot, Blizzard, Etsy, GitHub, Block, Bloomberg, Yelp, and more.
                    </p>

                    <div className='mx-auto mb-3 text-center'>
                      <VitessDiagram />
                    </div>

                    <blockquote className='mb-1'>
                      Our migration to Vitess is more than just a technological upgrade; it&apos;s a strategic move to
                      future-proof our database architecture for the next decade and beyond.
                    </blockquote>
                    <p className='text-secondary'>– Ryan Sherlock @Intercom</p>
                  </TabPanel>

                  <TabPanel unmount={false}>
                    <p>
                      Neki is our next-generation Postgres sharding architecture inspired by everything we have learned
                      building Vitess: a fresh design from first principles to bring massive scale and reliability to
                      Postgres workloads.
                    </p>

                    <ul className='my-0 mb-3 ml-2 flex flex-col gap-1 [&>li]:my-0'>
                      <li>Built for large scale Postgres workloads.</li>
                      <li>Designed for predictable performance and fault tolerance at scale.</li>
                      <li>
                        Get started with the{' '}
                        <a href='/docs/neki/tutorials/planetscale-quick-start-guide'>Neki quickstart</a> or read the{' '}
                        <a href='/docs/neki'>Neki documentation</a>.
                      </li>
                    </ul>

                    <p>
                      <a href='https://auth.planetscale.com/sign-up'>Sign up for a PlanetScale account</a> to use Neki
                      today.
                    </p>
                  </TabPanel>

                  <TabPanel unmount={false}>
                    <p>
                      <a href='/docs/postgres'>PlanetScale Postgres</a> starts at $5 per month and gives you a fully
                      managed PostgreSQL cluster built for performance and reliability, offering first-class tooling
                      without sacrificing developer experience. Available in AWS, GCP, and Azure. High availability
                      clusters deliver automatic failover across three availability zones.
                    </p>
                    <p>
                      <Link prefetch='intent' to='/metal'>
                        PlanetScale Metal
                      </Link>{' '}
                      starts at $50 per month for NVMe-backed performance with <strong>unlimited IOPS</strong>.
                    </p>

                    <ul className='my-0 mb-3 ml-2 flex flex-col gap-1 [&>li]:my-0'>
                      <li>
                        PlanetScale Metal{' '}
                        <Link prefetch='intent' to='/blog/benchmarking-postgres'>
                          is benchmarked as the fastest Postgres in the cloud
                        </Link>
                      </li>
                      <li>High availability: 1 primary and 2 replicas across three AZs with automated failover.</li>
                      <li>
                        Connection pooling with PgBouncer (port <code>6432</code>) and buffered failovers to minimize
                        impact.
                      </li>
                      <li>
                        Branch-per-environment; each branch runs on its own dedicated cluster, priced prorated to the
                        millisecond.
                      </li>
                      <li>
                        Scale reads with <a href='/docs/postgres/scaling/replicas'>replicas</a>, streamline schema work
                        with <a href='/docs/postgres/branching'>branching</a>, and protect data with{' '}
                        <a href='/docs/postgres/backups'>automated backups</a>.
                      </li>
                      <li>
                        <Link prefetch='intent' to='/blog/introducing-database-traffic-control'>
                          Database Traffic Control&trade;
                        </Link>{' '}
                        lets you enforce flexible budgets on your Postgres query traffic to protect your database from
                        runaway queries and unexpected load spikes.
                      </li>
                    </ul>

                    <p>
                      Get started with the{' '}
                      <a href='/docs/postgres/tutorials/planetscale-postgres-quickstart'>Postgres quickstart</a> or read
                      the <a href='/docs/postgres'>Postgres documentation</a>.
                    </p>
                  </TabPanel>
                </TabPanels>
              </TabGroup>
            </div>

            <div className='mt-3 flex flex-col gap-y-3'>
              <div>
                <h2 id='performance'>
                  <Link to='#performance'>Performance</Link>
                </h2>

                <p>
                  <Link prefetch='intent' to='/metal'>
                    PlanetScale Metal
                  </Link>{' '}
                  allows you to run your database on the fastest servers available in the cloud. Our blazing fast NVMe
                  drives unlock unlimited IOPS and drastically lower latencies compared to other cloud database
                  providers like Amazon Aurora and GCP Cloud SQL.
                </p>

                <figure>
                  <picture>
                    <source
                      srcSet={insights}
                      media='(prefers-color-scheme: light), (prefers-color-scheme: no-preference)'
                    />
                    <source srcSet={insightsDarkmode} media='(prefers-color-scheme: dark)' />
                    <img
                      alt='p95 graph dropping from 45 ms to between 5 and 10 ms'
                      loading='lazy'
                      height={1724}
                      width={3337}
                      src={insights}
                    />
                  </picture>
                  <figcaption>
                    The graph above displays the p50, p95, and p99 change after moving a database to Metal. See more
                    benchmarks on our{' '}
                    <Link prefetch='intent' to='/benchmarks/vitess'>
                      Benchmarking page
                    </Link>
                    .
                  </figcaption>
                  <br />
                  <blockquote className='mb-1'>
                    We are very happy with our decision to migrate to PlanetScale Metal which enabled us to achieve the
                    rare outcome of improvements in performance, cost, and reliability – a win for our customers and our
                    business.
                  </blockquote>
                  <p className='text-secondary'>– Aaron Young @ Cash App</p>
                </figure>
              </div>

              <hr className='dot-fill my-3' />
              <div>
                <h2 id='uptime'>
                  <Link to='#uptime'>Uptime</Link>
                </h2>

                <p>
                  Ensuring your database is always running and your data is always safe is our number one priority.
                  Nothing comes before uptime and reliability. Our{' '}
                  <Link prefetch='intent' to='/legal/planetscale-service-level-agreement'>
                    SLA commitment
                  </Link>{' '}
                  is 99.999% for multi-region deployments and 99.99% for single-region deployments.
                </p>

                <p>
                  PlanetScale’s platform far exceeds the reliability of database services like Amazon Aurora/RDS and
                  Google Cloud SQL with superior architecture and by making all database operations online.
                </p>
                <ul className='my-0 mb-3 ml-2 flex flex-col gap-1 [&>li]:my-0'>
                  <li>
                    <a href='/docs/vitess/schema-changes'>Deploy schema changes fully online</a>
                  </li>
                  <li>
                    <a href='/docs/vitess/schema-changes/deploy-requests#revert-a-schema-change'>
                      Revertable schema changes (with zero data loss)
                    </a>
                  </li>
                  <li>
                    <Link prefetch='intent' to='/blog/introducing-global-replica-credentials'>
                      Directing traffic to new read-only replicas
                    </Link>
                  </li>
                  <li>Online MySQL and Vitess version updates</li>
                  <li>
                    <a href='/docs/plans/cluster-sizing#upsizing-and-downsizing-clusters'>
                      Online cluster resizing and resharding
                    </a>
                  </li>
                </ul>

                <p>
                  You can check out our track record on our{' '}
                  <a href='https://www.planetscalestatus.com/' rel='nofollow noopener noreferrer' target='_blank'>
                    status page
                  </a>
                  .
                </p>
                <blockquote className='mb-1'>
                  We knew how deeply the PlanetScale team understands database services, and they were literally the
                  only team we trusted enough to consider leaving behind Aurora.
                </blockquote>
                <p className='text-secondary'>– Jamie Turner, Founder & CEO @ Convex</p>
              </div>

              <hr className='dot-fill my-3' />

              <div>
                <h2 id='cost'>
                  <Link to='#cost'>Cost</Link>
                </h2>

                <p>
                  At PlanetScale we believe cost is a unit of scale. Our product is less expensive than RDS MySQL and
                  Aurora for around 85% of the workloads that customers have migrated to Metal. Getting a custom quote
                  is easy:{' '}
                  <Link prefetch='intent' to='/contact'>
                    reach out to us
                  </Link>
                  . No long sales process or annoying pitches &mdash; you can probably tell from our website that the
                  tech speaks for itself.
                </p>

                <ul className='my-0 mb-3 ml-2 flex flex-col gap-1 [&>li]:my-0'>
                  <li>
                    No matter the size of your workload, PlanetScale Metal has the best price to performance ratio of
                    any database service.
                  </li>
                  <li>
                    Bring your own cloud with <a href='/docs/plans/managed'>PlanetScale Managed</a>
                  </li>
                  <li>
                    Purchase through the{' '}
                    <a
                      href='https://aws.amazon.com/marketplace/pp/prodview-luy3krhkpjne4'
                      rel='nofollow noopener noreferrer'
                      target='_blank'
                    >
                      AWS Marketplace
                    </a>{' '}
                    or the{' '}
                    <a
                      href='https://console.cloud.google.com/marketplace/product/planetscale-public/planetscale-database'
                      rel='nofollow noopener noreferrer'
                      target='_blank'
                    >
                      GCP Marketplace
                    </a>{' '}
                  </li>
                  <li>
                    Customers running Metal on PlanetScale Managed can{' '}
                    <Link
                      prefetch='intent'
                      to='/blog/planetscale-metal-theres-no-replacement-for-displacement#this-performance-comes-cheap'
                    >
                      realize additional savings
                    </Link>{' '}
                    through Reserved Instances or Savings Plans &mdash; discounts otherwise not available on traditional
                    EBS volumes.
                  </li>
                </ul>
              </div>

              <hr className='dot-fill my-3' />

              <div>
                <h2 id='security'>
                  <Link to='#security'>Security</Link>
                </h2>

                <p>
                  PlanetScale is trusted by some of the world’s largest brands. Our core infrastructure was built to
                  comply with high standards of security, compliance, and privacy.
                </p>

                <ul className='my-0 mb-3 ml-2 flex flex-col gap-1 [&>li]:my-0'>
                  <li>SOC 1 Type 2 & SOC 2 Type 2+ HIPAA compliance</li>
                  <li>PCI DSS 4.0 compliance as a Level 1 Service Provider</li>
                  <li>HIPAA Business Associate Agreements available on all plans</li>
                </ul>

                <p>
                  Learn more about our security and compliance practices in the{' '}
                  <a href='/docs/security'>security documentation</a>.
                </p>

                <blockquote className='mb-1'>
                  We have very strict regulatory requirements that can feel painful to the average engineer, however
                  PlanetScale was a strong partner in grinding through our asks, leaving us in a place where everyone
                  was happy.
                </blockquote>
                <p className='text-secondary'>– Aaron Young @Cash App</p>

                <p>
                  Visit our{' '}
                  <a href='https://trust.planetscale.com' rel='nofollow noopener noreferrer' target='_blank'>
                    Trust Center
                  </a>{' '}
                  to request the latest copy of our SOC reports, PCI DSS Attestation of Compliance, and more.
                </p>
              </div>

              <hr className='dot-fill my-3' />

              <div>
                <h2 id='platform'>
                  <Link to='#platform'>Features</Link>
                </h2>

                <p>
                  PlanetScale offers Vitess/MySQL, Neki, and Postgres, built by the infrastructure teams behind
                  Facebook, GitHub, Twitter, Slack, YouTube,{' '}
                  <Link prefetch='intent' to='/about'>
                    and more
                  </Link>
                  . The features below are shared across our products, followed by capabilities unique to each product.
                </p>

                <h3 className='mb-1'>Shared PlanetScale features</h3>
                <ul className='my-0 mb-3 ml-2 flex flex-col gap-1 [&>li]:my-0'>
                  <li>
                    <a href='/docs/postgres/branching'>Branching</a> and schema/data isolation workflows for safe
                    development across Vitess, Neki, and Postgres.
                  </li>
                  <li>
                    Read scaling with replicas in <a href='/docs/vitess/scaling/replicas'>Vitess</a>,{' '}
                    <a href='/docs/neki/replicas'>Neki</a>, and <a href='/docs/postgres/scaling/replicas'>Postgres</a>.
                  </li>
                  <li>
                    <Link prefetch='intent' to='/benchmarks'>
                      Incredible performance
                    </Link>{' '}
                    on PlanetScale Metal with unlimited IOPS on NVMe, plus network-attached storage options.
                  </li>
                  <li>
                    <a href='/docs/postgres/postgres-architecture'>High availability architecture</a> available with one
                    primary and two replicas across three availability zones and automated failover.
                  </li>
                  <li>
                    Built-in connection pooling and resilient connection behavior across our products. See{' '}
                    <a href='/docs/postgres-vs-vitess'>Postgres vs Vitess</a>.
                  </li>
                  <li>
                    Secure connectivity with <a href='/docs/postgres/connecting/ip-restrictions'>IP restrictions</a>,{' '}
                    <a href='/docs/postgres/connecting/private-connections/aws-privatelink'>AWS PrivateLink</a>, and{' '}
                    <a href='/docs/postgres/connecting/private-connections/gcp-private-service-connect'>
                      GCP Private Service Connect
                    </a>
                    .
                  </li>
                  <li>
                    Built-in observability with <a href='/docs/vitess/monitoring/query-insights'>Query Insights</a> for
                    Vitess, <a href='/docs/neki/monitoring/query-insights'>Query Insights</a> for Neki, and{' '}
                    <a href='/docs/postgres/monitoring/query-insights'>Query Insights</a> for Postgres.
                  </li>
                  <li>
                    Automated and custom backups in <a href='/docs/vitess/backups'>Vitess</a>,{' '}
                    <a href='/docs/neki/backups'>Neki</a>, and <a href='/docs/postgres/backups'>Postgres</a>.
                  </li>
                  <li>
                    Multi-region support for globally distributed workloads. See{' '}
                    <a href='/docs/postgres-vs-vitess'>Postgres vs Vitess</a>.
                  </li>
                  <li>
                    <a href='/docs/ai-tooling'>AI agent</a> driven database optimization.
                  </li>
                  <li>
                    Safe agent access with the <a href='/docs/mcp-server'>PlanetScale MCP server</a>.
                  </li>
                </ul>

                <h3 className='mb-1'>PlanetScale Vitess features</h3>
                <ul className='my-0 mb-3 ml-2 flex flex-col gap-1 [&>li]:my-0'>
                  <li>Deploy requests for zero downtime schema changes that your team can review.</li>
                  <li>
                    <Link prefetch='intent' to='/blog/behind-the-scenes-how-schema-reverts-work'>
                      Revert
                    </Link>{' '}
                    bad schema changes with no downtime and no data loss.
                  </li>
                  <li>
                    Full database observability <a href='/docs/vitess/monitoring/query-insights'>with Insights</a> to
                    give you a detailed overview of cluster health.
                  </li>
                  <li>
                    Automate the horizontal scaling of your database with our{' '}
                    <a href='/docs/vitess/sharding/sharding-quickstart'>explicit sharding workflows</a>.
                  </li>
                  <li>
                    Read-only regions and our Global Edge Network route read traffic to local nodes with low latency.
                  </li>
                  <li>
                    Integrations with Fivetran, Airbyte, Hightouch, Datadog, Vantage, Debezium,{' '}
                    <a href='/docs/vitess/etl'>and more</a>.
                  </li>
                </ul>

                <h3 className='mb-1'>PlanetScale Neki features</h3>
                <ul className='my-0 mb-3 ml-2 flex flex-col gap-1 [&>li]:my-0'>
                  <li>
                    Horizontal sharding for Postgres with a first-principles architecture. See{' '}
                    <Link prefetch='intent' to='/neki'>
                      Neki
                    </Link>
                    .
                  </li>
                  <li>
                    A <a href='/docs/neki/data-topology'>data topology</a> that describes how tables and queries are
                    distributed across shards.
                  </li>
                  <li>
                    Online <a href='/docs/neki/schema-changes'>schema changes</a> and{' '}
                    <a href='/docs/neki/branching'>branching</a> for isolated development environments.
                  </li>
                  <li>
                    Built-in <a href='/docs/neki/monitoring/query-insights'>Query Insights</a>,{' '}
                    <a href='/docs/neki/backups'>backups</a>, and the rest of the PlanetScale platform.
                  </li>
                </ul>

                <h3 id='postgres-features' className='mb-1'>
                  PlanetScale Postgres features
                </h3>

                <ul className='my-0 mb-3 ml-2 flex flex-col gap-1 [&>li]:my-0'>
                  <li>$5 single node databases available for development, prototyping, and small projects.</li>
                  <li>
                    <a href='/docs/postgres/backups/point-in-time-recovery'>Point-in-time recovery</a> for restoring to
                    a specific moment.
                  </li>
                  <li>
                    Horizontal scaling with <a href='/docs/neki'>Neki</a>.
                  </li>
                  <li>
                    Flexible cluster configuration including{' '}
                    <a href='/docs/postgres/cluster-configuration/cpu-architectures'>CPU architectures</a>,{' '}
                    <a href='/docs/postgres/cluster-configuration/cluster-storage'>storage profiles</a>,{' '}
                    <a href='/docs/postgres/cluster-configuration/parameters'>parameter tuning</a>, and{' '}
                    <a href='/docs/postgres/cluster-configuration/disk-autoscaling'>disk autoscaling</a>.
                  </li>
                  <li>
                    Advanced safety guardrails with <a href='/docs/postgres/extensions/pg_strict'>pg_strict</a> to help
                    block accidental mass UPDATE or DELETE statements without WHERE clauses.
                  </li>
                  <li>
                    Monitoring integrations for <a href='/docs/postgres/monitoring/prometheus-postgres'>Prometheus</a>,{' '}
                    <a href='/docs/postgres/monitoring/prometheus-metrics-datadog-postgres'>Datadog</a>, and{' '}
                    <a href='/docs/postgres/monitoring/pganalyze'>pganalyze</a>.
                  </li>
                  <li>
                    Curated <a href='/docs/postgres/extensions'>Postgres extensions</a> including pg_cron, pg_hint_plan,
                    pg_partman_bgw, pg_squeeze, pg_stat_statements,{' '}
                    <a href='/docs/postgres/extensions/pg_strict'>pg_strict</a>, pginsights, pg_duckdb, and TimescaleDB.
                  </li>
                  <li>
                    <a href='/docs/postgres/integrations/logical-cdc'>Logical CDC integrations</a> and guided
                    no-downtime import options including{' '}
                    <a href='/docs/postgres/imports/postgres-imports'>dump/restore and WAL streaming</a>, plus
                    provider-specific paths from Supabase, Neon, Heroku, and Aurora.
                  </li>
                  <li>
                    <a href='/docs/postgres/traffic-control'>Database Traffic Control&trade;</a> to set flexible budgets
                    on your Postgres query traffic, guarding against runaway queries and unexpected load spikes.
                  </li>
                </ul>
              </div>
              <blockquote className='mb-1'>
                Databases are hard. We would rather PlanetScale manage them. We wanted the support PlanetScale offers
                because they are the experts in the field. We’ve seen this come to fruition in our relationship.
              </blockquote>
              <p className='text-secondary'>– Chris Karper @ MyFitnessPal</p>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </>
  )
}
